import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  Investment,
  InvestmentWithProject,
  isInvestmentStatusRejected,
} from '@app/models/investment.models';
import { Project } from '@app/models/project.models';
import {
  ACTUALLY_LOADING_SATE,
  DEAFAULT_LOADING_STATE,
  LOADED_LOADING_STATE,
  ProjectDataRequest,
  RELOADING_LOADING_STATE,
} from '@app/models/shared.models';
import {
  ProjectsPageFiltersSnapshot,
  ProjectsPageFiltersSyncService,
} from '@app/pages/projects/projects-page-filters-sync.service';
import { ProjectService } from '@app/services/projects/service';
import { GenericStore } from '@app/stores/generic.store';
import { GenericProjectStore } from '@app/stores/projects/generic-project.store';
import { ProjectsStore } from '@app/stores/projects/projects.store';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { ValidatedInputComponent } from '@app/ui-components/validated-input/validated-input.component';
import {
  NgbNavModule,
  NgbOffcanvas,
  NgbOffcanvasRef,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { CardComponent } from '../common/card/card.component';
import { PaginationComponent } from '../common/pagination/pagination.component';
import { ProjectCardComponent } from '../common/project-card/project-card.component';
import { ValidatedDropdownComponent } from '../common/validated-dropdown/validated-dropdown.component';
import { ProjectListComponent } from '../project-list/project-list.component';
import { ProjectDisplayType } from '../project-vertical-card/project-vertical-card.component';
import {
  MultiSelectComponent,
  SelectionItemWithId,
} from '../search/multi-select/multi-select.component';

export type ProjectsLayout = 'list' | 'grid';

const ALL_INVESTMENTS_TAKE = 10000;

export interface ProjectFilterFormGroup {
  interestMin: FormControl<string | null>;
  interestMax: FormControl<string | null>;
  yieldMin: FormControl<string | null>;
  yieldMax: FormControl<string | null>;
  durationMin: FormControl<string | null>;
  durationMax: FormControl<string | null>;
}

@Component({
  selector: 'app-projects-with-filters',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    NgbNavModule,
    CardComponent,
    MultiSelectComponent,
    ProjectListComponent,
    ProjectCardComponent,
    ButtonComponent,
    ValidatedInputComponent,
    ValidatedDropdownComponent,
    PaginationComponent,
  ],
  templateUrl: './projects-with-filters.component.html',
  styleUrl: './projects-with-filters.component.scss',
})
export class ProjectsWithFiltersComponent implements OnInit, OnDestroy {
  private static readonly numericOptionalValidator = Validators.pattern(
    /^(-?(\d+([,.]\d*)?|\d*[,.]\d+))?$/,
  );
  private static readonly defaultProjectsPageSizeOptions = [10, 20, 50];

  public displayType = input.required<ProjectDisplayType>();
  public showFilter = input<boolean>(true);
  public showGoToProjects = model<boolean>(true);
  public initialPageSize = input<number | undefined>(undefined);

  public hideListCard = input(false);
  public projectsPageFilterSync = input(false);
  public showProjectsListingHeader = input(false);
  public projectsPageSizeOptionsInput = input<number[]>(
    ProjectsWithFiltersComponent.defaultProjectsPageSizeOptions,
  );

  protected listCardTitleKey = computed((): string => {
    switch (this.displayType()) {
      case 'admin':
        return 'admin.sideNav.projects.allProjects';
      case 'acceptedInvestments':
        return 'profile.investments.accepted';
      case 'pendingInvestments':
        return 'profile.investments.pending';
      case 'declinedInvestments':
        return 'profile.investments.rejected';
      case 'investments':
        return 'profile.investments.title';
      case 'followed':
        return 'profile.watchedProjects.title';
      case 'followedUnderPreparation':
        return 'profile.watchedProjects.inPreparation';
      case 'myProjects':
        return 'profile.myProjects.title';
      case 'myRunningProjects':
        return 'profile.myProjects.fundraising';
      case 'myUnderPreparationProjects':
        return 'profile.myProjects.inPreparation';
      case 'myUnderVerificationProjects':
        return 'profile.myProjects.underReview';
      case 'public':
        return 'projects.actualProjects';
      case 'underPreparation':
        return 'projects.projectsInPreparation';
      case 'closed':
        return 'projects.closedProjects';
      default:
        return 'profile.myProjects.title';
    }
  });

  protected projects = signal<Project[] | undefined>(undefined);
  protected investments = signal<InvestmentWithProject[]>([]);
  protected investmentsLoadingState = signal(DEAFAULT_LOADING_STATE);
  protected currentPage = model(1);
  protected pageSize = signal(10);
  protected skip = computed(
    () => ((this.currentPage() ?? 1) - 1) * (this.pageSize() ?? 10),
  );
  protected take = this.pageSize;

  protected filteredInvestments = computed(() => {
    const invs = this.investments();
    const displayType = this.displayType();
    switch (displayType) {
      case 'acceptedInvestments':
      case 'pendingInvestments':
        return invs;
      case 'declinedInvestments':
        return invs.filter((i) => isInvestmentStatusRejected(i.status));
      default:
        return [];
    }
  });

  protected projectsLayout = computed((): ProjectsLayout => {
    switch (this.displayType()) {
      case 'myProjects':
      case 'myRunningProjects':
      case 'investments':
      case 'followed':
      case 'followedUnderPreparation':
      case 'acceptedInvestments':
      case 'pendingInvestments':
      case 'declinedInvestments':
      case 'admin':
        return 'grid';
      default:
        return 'list';
    }
  });

  protected getEmptyText = computed(() => {
    const displayType = this.displayType();

    switch (displayType) {
      case 'public':
      case 'myProjects':
      case 'closed':
      case 'underPreparation':
      case 'admin':
        return 'profile.myProjects.noProjects';
      case 'acceptedInvestments':
      case 'pendingInvestments':
      case 'declinedInvestments':
      case 'investments':
        return 'profile.investments.noInvestments';
      case 'followed':
      case 'followedUnderPreparation':
        return 'profile.watchedProjects.noWatchedProjects';
      case 'myRunningProjects':
      case 'myUnderPreparationProjects':
      case 'myUnderVerificationProjects':
        return 'profile.myProjects.noProjects';
    }
  });

  protected loadingState = computed(() => {
    const displayType = this.displayType();

    switch (displayType) {
      case 'public':
        return this.projectStore.ongoingProjectLoadingState();
      case 'myProjects':
        return this.projectStore.ownProjectLoadingState();
      case 'closed':
        return this.projectStore.closedProjectLoadingState();
      case 'underPreparation':
        return this.projectStore.preparedProjectLoadingState();
      case 'admin':
        return this.projectStore.adminLoadingState();
      case 'acceptedInvestments':
      case 'pendingInvestments':
        return this.investmentsLoadingState();
      case 'declinedInvestments': {
        const inv = this.investmentsLoadingState();
        const proj = this.projectStore.ownInvestedProjectLoadingState();
        const anyLoading = inv.loading || proj.loading;
        const anyLoaded = inv.loaded || proj.loaded;
        if (anyLoading && anyLoaded) return RELOADING_LOADING_STATE;
        if (anyLoading) return ACTUALLY_LOADING_SATE;
        if (inv.loaded && proj.loaded) return LOADED_LOADING_STATE;
        return DEAFAULT_LOADING_STATE;
      }
      case 'investments':
        return this.projectStore.ownInvestedProjectLoadingState();
      case 'followed':
      case 'followedUnderPreparation':
        return this.projectStore.followedProjectLoadingState();
      case 'myRunningProjects':
      case 'myUnderPreparationProjects':
      case 'myUnderVerificationProjects':
        return this.projectStore.ownProjectLoadingState();
    }
  });

  protected showLoadedBody = computed(() => {
    const s = this.loadingState();
    return s.loaded && !s.loading;
  });

  protected active = 1;
  protected projectStore = inject(ProjectsStore);
  private readonly projectsPageSync = inject(ProjectsPageFiltersSyncService, {
    optional: true,
  });
  private readonly offcanvasService = inject(NgbOffcanvas);
  private filterOffcanvasRef: NgbOffcanvasRef | null = null;
  protected projectService = inject(ProjectService);
  protected genericDataStore = inject(GenericStore);
  protected genericProjectStore = inject(GenericProjectStore);
  protected incomingOffersCountMap = signal<Record<string, number>>({});

  protected appliedFilters = signal<Partial<ProjectDataRequest>>({});
  protected readonly adminPageSizeOptions = [10, 20, 50];
  private readonly adminSearchSubject = new Subject<string>();
  protected adminSearchDraft = signal('');
  protected adminSearchQuery = signal('');
  protected adminSortNewestFirst = signal(true);
  protected projectsSortNewestFirst = signal(true);
  protected projectsPageSizeOptions = computed(() => {
    const options = this.projectsPageSizeOptionsInput();
    return options.length > 0
      ? options
      : ProjectsWithFiltersComponent.defaultProjectsPageSizeOptions;
  });
  protected showListingHeader = computed(
    () => this.showProjectsListingHeader() && this.isProjectsPageDisplayType(),
  );
  protected selectedCurrencies = model<SelectionItemWithId[]>([]);
  protected selectedLocations = model<SelectionItemWithId[]>([]);
  protected selectedCategories = model<SelectionItemWithId[]>([]);
  protected fb = inject(FormBuilder);
  protected filterForm = this.fb.group<ProjectFilterFormGroup>({
    interestMin: this.fb.control<string | null>(null, [
      ProjectsWithFiltersComponent.numericOptionalValidator,
    ]),
    interestMax: this.fb.control<string | null>(null, [
      ProjectsWithFiltersComponent.numericOptionalValidator,
    ]),
    yieldMin: this.fb.control<string | null>(null, [
      ProjectsWithFiltersComponent.numericOptionalValidator,
    ]),
    yieldMax: this.fb.control<string | null>(null, [
      ProjectsWithFiltersComponent.numericOptionalValidator,
    ]),
    durationMin: this.fb.control<string | null>(null, [
      ProjectsWithFiltersComponent.numericOptionalValidator,
    ]),
    durationMax: this.fb.control<string | null>(null, [
      ProjectsWithFiltersComponent.numericOptionalValidator,
    ]),
  });

  protected adminPageSizeControl = new FormControl<number>(10, {
    nonNullable: true,
  });
  protected projectsPageSizeControl = new FormControl<number>(10, {
    nonNullable: true,
  });
  protected adminSortOrderControl = new FormControl<'newest' | 'oldest'>(
    'newest',
    { nonNullable: true },
  );
  protected projectsSortOrderControl = new FormControl<'newest' | 'oldest'>(
    'newest',
    { nonNullable: true },
  );

  protected currencies = computed(() =>
    this.genericDataStore.currenciesEntities().map(
      (currency): SelectionItemWithId => ({
        id: currency.id,
        text: currency.code,
      }),
    ),
  );
  protected locations = computed(() =>
    this.genericDataStore.countriesEntities().map(
      (country): SelectionItemWithId => ({
        id: country.id,
        text: country.code,
      }),
    ),
  );
  protected categories = computed(() =>
    this.genericProjectStore.projectLoanTermEntities().map(
      (term): SelectionItemWithId => ({
        id: String(term.id),
        text: this.translateService.instant(
          'generic.projectLoanTerm.' + term.name,
        ),
      }),
    ),
  );

  protected totalCount = computed(() => {
    const displayType = this.displayType();
    switch (displayType) {
      case 'public':
        return this.projectStore.ongoingProjectTotal();
      case 'myProjects':
        return this.projectStore.ownProjectTotal();
      case 'closed':
        return this.projectStore.closedProjectTotal();
      case 'underPreparation':
        return this.projectStore.preparedProjectTotal();
      case 'admin':
        return this.projectStore.adminProjectTotal();
      case 'acceptedInvestments':
      case 'pendingInvestments':
      case 'declinedInvestments':
        return this.filteredInvestments().length;
      case 'investments':
        return this.projectStore.ownInvestedProjectTotal();
      case 'followed':
      case 'followedUnderPreparation':
        return this.projectStore.followedProjectTotal();
      case 'myRunningProjects':
      case 'myUnderPreparationProjects':
      case 'myUnderVerificationProjects':
        return this.projectStore.ownProjectTotal();
    }
    return 0;
  });

  protected numberOfPages = computed(() => {
    const total = this.totalCount();
    const size = this.pageSize();
    if (size === 0) return 0;
    return Math.ceil(total / size);
  });

  protected hasItems = computed(() => {
    if (this.isInvestmentCardDisplayType()) {
      return this.filteredInvestments().length > 0;
    }

    return (this.projects()?.length ?? 0) > 0;
  });

  protected activeFilterCount = computed<number>(() => {
    const applied = this.appliedFilters();
    let count = 0;
    count += this.selectedCurrencies().length;
    count += this.selectedLocations().length;
    count += this.selectedCategories().length;
    if (applied.interest) count += 1;
    if (applied.yield) count += 1;
    if (applied.duration) count += 1;
    return count;
  });

  constructor(
    private router: Router,
    private translateService: TranslateService,
  ) {
    this.adminSearchSubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((q) => this.adminSearchQuery.set(q));

    effect(() => {
      const displayType = this.displayType();
      const skip = this.skip();
      const take = this.take();
      this.appliedFilters();
      if (displayType === 'admin') {
        this.adminSortNewestFirst();
        this.adminSearchQuery();
      }
      if (this.isProjectsPageDisplayType(displayType)) {
        this.projectsSortNewestFirst();
      }

      untracked(() => {
        const base = { skip, take };
        switch (displayType) {
          case 'public':
            this.projectStore.loadOngoingProjects(
              this.buildRequestWithFilters(skip, take),
            );
            break;
          case 'myProjects':
            this.projectStore.loadOwnProjects(base);
            break;
          case 'closed':
            this.projectStore.loadClosedProjects(
              this.buildRequestWithFilters(skip, take),
            );
            break;
          case 'underPreparation':
            this.projectStore.loadPreparedProjects(
              this.buildRequestWithFilters(skip, take),
            );
            break;
          case 'admin':
            this.projectStore.loadAdminProjects(
              this.buildRequestWithFilters(skip, take),
            );
            break;
          case 'acceptedInvestments': {
            this.investmentsLoadingState.set(ACTUALLY_LOADING_SATE);
            this.projectService
              .getAcceptedInvestments({ skip: 0, take: ALL_INVESTMENTS_TAKE })
              .subscribe({
                next: (res) => {
                  const mapped: InvestmentWithProject[] = res.data.map((d) => ({
                    ...d,
                    accountOwner: d.bankAccountName ?? d.accountOwner,
                    accountNumber: d.bankAccountNumber ?? d.accountNumber,
                    project: d.project,
                  }));
                  this.investments.set(mapped);
                  const skip = Math.max(0, this.skip() ?? 0);
                  const take = Math.max(1, this.take() ?? 10);
                  const page = mapped.slice(skip, skip + take);
                  const projectsForPage = page
                    .map((inv) => inv.project)
                    .filter((p): p is Project => p != null && p !== undefined);
                  this.projects.set(projectsForPage);
                  this.investmentsLoadingState.set(LOADED_LOADING_STATE);
                },
                error: () => {
                  this.investments.set([]);
                  this.projects.set([]);
                  this.investmentsLoadingState.set(LOADED_LOADING_STATE);
                },
              });
            break;
          }
          case 'pendingInvestments':
            this.loadPendingInvestmentsData();
            break;
          case 'declinedInvestments': {
            this.investmentsLoadingState.set(ACTUALLY_LOADING_SATE);
            this.projectStore.loadOwnInvestedProjects({
              skip: 0,
              take: ALL_INVESTMENTS_TAKE,
            });
            this.projectService
              .getInvestments({ skip: 0, take: ALL_INVESTMENTS_TAKE })
              .subscribe({
                next: (res) => {
                  this.investments.set(res.data);
                  this.investmentsLoadingState.set(LOADED_LOADING_STATE);
                },
                error: () => {
                  this.investments.set([]);
                  this.investmentsLoadingState.set(LOADED_LOADING_STATE);
                },
              });
            break;
          }
          case 'investments':
            this.projectStore.loadOwnInvestedProjects(base);
            break;
          case 'followed':
          case 'followedUnderPreparation':
            this.projectStore.loadFollowedProjects(base);
            break;
          case 'myRunningProjects':
          case 'myUnderPreparationProjects':
          case 'myUnderVerificationProjects':
            this.projectStore.loadOwnProjects(base);
            break;
        }
      });
    });

    effect(() => {
      const displayType = this.displayType();

      switch (displayType) {
        case 'public': {
          this.showGoToProjects.set(false);
          this.projects.set(this.projectStore.ongoingProjectEntities());
          return;
        }
        case 'myProjects':
          this.projects.set(this.projectStore.ownProjectEntities());
          return;
        case 'closed': {
          this.showGoToProjects.set(false);
          this.projects.set(this.projectStore.closedProjectEntities());
          return;
        }
        case 'underPreparation': {
          this.projects.set(this.projectStore.preparedProjectEntities());
          return;
        }
        case 'admin':
          this.showGoToProjects.set(false);
          this.projects.set(this.projectStore.adminProjectEntities());
          return;
        case 'acceptedInvestments': {
          const list = this.filteredInvestments();
          const skip = this.skip();
          const take = this.take();
          const page = list.slice(skip, skip + take);
          const projectsForPage = page
            .map((inv) => (inv as InvestmentWithProject).project)
            .filter((p): p is Project => p != null && p !== undefined);
          this.projects.set(projectsForPage);
          return;
        }
        case 'pendingInvestments': {
          const list = this.filteredInvestments();
          const skip = this.skip();
          const take = this.take();
          const page = list.slice(skip, skip + take);
          const projectsForPage = page
            .map((inv) => (inv as InvestmentWithProject).project)
            .filter((p): p is Project => p != null && p !== undefined);
          this.projects.set(projectsForPage);
          return;
        }
        case 'declinedInvestments': {
          const list = this.filteredInvestments();
          const skip = this.skip();
          const take = this.take();
          const page = list.slice(skip, skip + take);
          const entities = this.projectStore.ownInvestedProjectEntities();
          const projectsForPage = page
            .map((inv) =>
              entities.find((p) => p.id === (inv.projectId ?? undefined)),
            )
            .filter((p): p is Project => p != null);
          this.projects.set(projectsForPage);
          return;
        }
        case 'investments':
          this.projects.set(this.projectStore.ownInvestedProjectEntities());
          return;
        case 'followed':
        case 'followedUnderPreparation':
          this.showGoToProjects.set(true);
          this.projects.set(this.projectStore.followedProjectEntities());
          return;
        case 'myRunningProjects':
        case 'myUnderPreparationProjects':
        case 'myUnderVerificationProjects':
          this.projects.set(this.projectStore.ownProjectEntities());
          return;
      }
    });

    effect(() => {
      const displayType = this.displayType();
      if (displayType !== 'myRunningProjects') {
        this.incomingOffersCountMap.set({});
        return;
      }

      this.projectService
        .getInvestments({
          skip: 0,
          take: ALL_INVESTMENTS_TAKE,
        })
        .subscribe({
          next: (response) => {
            this.incomingOffersCountMap.set(
              this.aggregateIncomingOffersCounts(response?.data ?? []),
            );
          },
          error: () => {
            this.incomingOffersCountMap.set({});
          },
        });
    });

    effect(() => {
      if (!this.projectsPageFilterSync()) {
        return;
      }
      if (!this.shouldPersistProjectsPageSnapshot()) {
        return;
      }
      this.currentPage();
      this.pageSize();
      this.appliedFilters();
      this.selectedCurrencies();
      this.selectedLocations();
      this.selectedCategories();
      untracked(() => {
        this.persistProjectsPageSnapshot();
      });
    });
  }

  ngOnInit() {
    const initial = this.initialPageSize();
    if (this.displayType() === 'admin' && initial != null) {
      this.pageSize.set(initial);
    }

    if (
      this.projectsPageFilterSync() &&
      this.shouldPersistProjectsPageSnapshot() &&
      this.projectsPageSync
    ) {
      const snap = this.projectsPageSync.snapshot();
      if (snap) {
        this.applyProjectsPageFiltersSnapshot(snap);
      }
    }

    this.adminPageSizeControl.setValue(this.pageSize(), { emitEvent: false });
    this.projectsPageSizeControl.setValue(this.pageSize(), {
      emitEvent: false,
    });
    this.adminSortOrderControl.setValue(
      this.adminSortNewestFirst() ? 'newest' : 'oldest',
      { emitEvent: false },
    );
    this.projectsSortOrderControl.setValue(
      this.projectsSortNewestFirst() ? 'newest' : 'oldest',
      { emitEvent: false },
    );
  }

  ngOnDestroy(): void {
    this.persistProjectsPageSnapshot();
  }

  private shouldPersistProjectsPageSnapshot(): boolean {
    if (!this.projectsPageFilterSync()) {
      return false;
    }
    const t = this.displayType();
    return t === 'public' || t === 'underPreparation' || t === 'closed';
  }

  private buildProjectsPageFiltersSnapshot(): ProjectsPageFiltersSnapshot {
    const v = this.filterForm.getRawValue();
    return {
      appliedFilters: { ...this.appliedFilters() },
      interestMin: v.interestMin,
      interestMax: v.interestMax,
      yieldMin: v.yieldMin,
      yieldMax: v.yieldMax,
      durationMin: v.durationMin,
      durationMax: v.durationMax,
      currencies: [...this.selectedCurrencies()],
      locations: [...this.selectedLocations()],
      categories: [...this.selectedCategories()],
      currentPage: this.currentPage() ?? 1,
      pageSize: this.pageSize(),
    };
  }

  private applyProjectsPageFiltersSnapshot(
    s: ProjectsPageFiltersSnapshot,
  ): void {
    this.appliedFilters.set(s.appliedFilters ?? {});
    this.selectedCurrencies.set(s.currencies ?? []);
    this.selectedLocations.set(s.locations ?? []);
    this.selectedCategories.set(s.categories ?? []);
    this.filterForm.patchValue(
      {
        interestMin: s.interestMin ?? null,
        interestMax: s.interestMax ?? null,
        yieldMin: s.yieldMin ?? null,
        yieldMax: s.yieldMax ?? null,
        durationMin: s.durationMin ?? null,
        durationMax: s.durationMax ?? null,
      },
      { emitEvent: false },
    );
    this.currentPage.set(s.currentPage ?? 1);
    this.pageSize.set(s.pageSize ?? 10);
  }

  private persistProjectsPageSnapshot(): void {
    if (!this.shouldPersistProjectsPageSnapshot()) {
      return;
    }
    if (!this.projectsPageSync) {
      return;
    }
    this.projectsPageSync.save(this.buildProjectsPageFiltersSnapshot());
  }

  protected buildRequestWithFilters(
    skip: number,
    take: number,
  ): ProjectDataRequest {
    const filters = this.appliedFilters();
    const displayType = this.displayType();
    return {
      skip,
      take,
      filter:
        displayType === 'admin'
          ? this.adminSearchQuery().trim() || undefined
          : undefined,
      sort:
        displayType === 'admin' || this.isProjectsPageDisplayType(displayType)
          ? [
              {
                selector: 'createdAt',
                desc:
                  displayType === 'admin'
                    ? this.adminSortNewestFirst()
                    : this.projectsSortNewestFirst(),
              },
            ]
          : undefined,
      currency: filters.currency?.length ? filters.currency : undefined,
      country: filters.country?.length ? filters.country : undefined,
      category: filters.category?.length ? filters.category : undefined,
      interest: filters.interest?.trim() || undefined,
      yield: filters.yield?.trim() || undefined,
      duration: filters.duration?.trim() || undefined,
    };
  }

  protected onAdminPageSizeSelectionChanged(value: unknown): void {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return;
    this.pageSize.set(n);
    this.currentPage.set(1);
  }

  protected onAdminSortSelectionChanged(value: unknown): void {
    const v = value === 'oldest' ? 'oldest' : 'newest';
    this.adminSortNewestFirst.set(v === 'newest');
    this.currentPage.set(1);
  }

  protected onProjectsPageSizeSelectionChanged(value: unknown): void {
    const n = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(n)) return;
    this.pageSize.set(n);
    this.currentPage.set(1);
  }

  protected onProjectsSortSelectionChanged(value: unknown): void {
    const v = value === 'oldest' ? 'oldest' : 'newest';
    this.projectsSortNewestFirst.set(v === 'newest');
    this.currentPage.set(1);
  }

  protected onAdminSearchInput(value: string): void {
    this.adminSearchDraft.set(value);
    this.adminSearchSubject.next(value);
  }

  protected rangeStr(min: string, max: string): string | undefined {
    const a = min.trim();
    const b = max.trim();
    if (!a && !b) return undefined;
    const minPart = a || 'null';
    const maxPart = b || 'null';
    return `${minPart}-${maxPart}`;
  }

  protected applyFilters(): void {
    const v = this.filterForm.getRawValue();
    const str = (x: string | null | undefined) =>
      x != null ? String(x).trim() : '';
    this.appliedFilters.set({
      currency:
        this.selectedCurrencies().length > 0
          ? this.selectedCurrencies().map((c) => c.text)
          : undefined,
      country:
        this.selectedLocations().length > 0
          ? this.selectedLocations().map((c) => c.text)
          : undefined,
      category:
        this.selectedCategories().length > 0
          ? this.selectedCategories().map((c) => c.id)
          : undefined,
      interest:
        this.rangeStr(str(v.interestMin), str(v.interestMax)) || undefined,
      yield: this.rangeStr(str(v.yieldMin), str(v.yieldMax)) || undefined,
      duration:
        this.rangeStr(str(v.durationMin), str(v.durationMax)) || undefined,
    });
    this.currentPage.set(1);
    this.persistProjectsPageSnapshot();
    this.filterOffcanvasRef?.dismiss();
  }

  protected clearFilters(): void {
    this.appliedFilters.set({});
    this.adminSearchDraft.set('');
    this.adminSearchQuery.set('');
    this.adminSearchSubject.next('');
    this.selectedCurrencies.set([]);
    this.selectedLocations.set([]);
    this.selectedCategories.set([]);
    this.filterForm.reset({
      interestMin: null,
      interestMax: null,
      yieldMin: null,
      yieldMax: null,
      durationMin: null,
      durationMax: null,
    });
    this.currentPage.set(1);
    this.persistProjectsPageSnapshot();
    this.filterOffcanvasRef?.dismiss();
  }

  protected openFilterOffcanvas(tpl: TemplateRef<unknown>): void {
    if (this.filterOffcanvasRef) {
      return;
    }
    const ref = this.offcanvasService.open(tpl, {
      position: 'start',
      panelClass: 'projects-filter-offcanvas',
    });
    this.filterOffcanvasRef = ref;
    const cleanup = () => {
      if (this.filterOffcanvasRef === ref) {
        this.filterOffcanvasRef = null;
      }
    };
    ref.result.then(cleanup, cleanup);
  }

  @HostListener('window:resize')
  protected onWindowResize(): void {
    if (window.innerWidth >= 992 && this.filterOffcanvasRef) {
      this.filterOffcanvasRef.dismiss();
    }
  }

  public goToProjects(): void {
    this.router.navigate(['/projects']);
  }

  protected getInvestment(projectId: string): Investment | undefined {
    return this.investments().find((i) => i.projectId === projectId);
  }

  protected getPagedInvestmentsForCards(): InvestmentWithProject[] {
    const skip = this.skip();
    const take = this.take();
    return this.filteredInvestments().slice(skip, skip + take);
  }

  protected getProjectForInvestmentCard(
    investment: InvestmentWithProject,
  ): Project | undefined {
    if (investment.project != null) {
      return investment.project;
    }

    const projectId = investment.projectId;
    if (!projectId) {
      return undefined;
    }

    return this.projectStore
      .ownInvestedProjectEntities()
      .find((project) => project.id === projectId);
  }

  protected isInvestmentCardDisplayType(): boolean {
    const t = this.displayType();
    return (
      t === 'acceptedInvestments' ||
      t === 'pendingInvestments' ||
      t === 'declinedInvestments'
    );
  }

  protected isProjectsPageDisplayType(
    displayType = this.displayType(),
  ): boolean {
    return (
      displayType === 'public' ||
      displayType === 'underPreparation' ||
      displayType === 'closed'
    );
  }

  protected getProjectsForList(): Project[] {
    return (this.projects() ?? []) as Project[];
  }

  protected getIncomingOffersCount(projectId: string): number {
    return this.incomingOffersCountMap()[projectId] ?? 0;
  }

  public onIncomingOffersClicked(projectId: string): void {
    void this.router.navigate(['/project', projectId, 'offers', 'details']);
  }

  public onPublicDetailsClicked(projectId: string): void {
    void this.router.navigate(['/project', projectId]);
  }

  public onEditWizardClicked(projectId: string): void {
    void this.router.navigate(['/project-wizard', projectId]);
  }

  public onPendingInvestmentCancelled(): void {
    if (this.displayType() === 'pendingInvestments') {
      this.loadPendingInvestmentsData();
    }
  }

  public onDetailsClicked($event: string): void {
    const displayType = this.displayType();

    switch (displayType) {
      case 'public':
      case 'myProjects':
      case 'closed':
      case 'underPreparation':
      case 'myRunningProjects':
      case 'myUnderVerificationProjects':
      case 'acceptedInvestments':
      case 'pendingInvestments':
      case 'declinedInvestments':
      case 'investments':
        this.router.navigate(['/project', $event]);
        return;
      case 'myUnderPreparationProjects':
        this.router.navigate(['/project-wizard', $event]);
        return;

      case 'admin':
        this.router.navigate(['/admin/projects', $event]);
        return;
      case 'followed':
      case 'followedUnderPreparation':
        return;
    }
  }

  private loadPendingInvestmentsData(): void {
    this.investmentsLoadingState.set(ACTUALLY_LOADING_SATE);
    this.projectService
      .getPendingInvestments({ skip: 0, take: ALL_INVESTMENTS_TAKE })
      .subscribe({
        next: (res) => {
          const mapped: InvestmentWithProject[] = res.data.map((d) => ({
            ...d,
            accountOwner: d.bankAccountName ?? d.accountOwner,
            accountNumber: d.bankAccountNumber ?? d.accountNumber,
            project: d.project,
          }));
          this.investments.set(mapped);
          const skip = Math.max(0, this.skip() ?? 0);
          const take = Math.max(1, this.take() ?? 10);
          const page = mapped.slice(skip, skip + take);
          const projectsForPage = page
            .map((inv) => inv.project)
            .filter((p): p is Project => p != null && p !== undefined);
          this.projects.set(projectsForPage);
          this.investmentsLoadingState.set(LOADED_LOADING_STATE);
        },
        error: () => {
          this.investments.set([]);
          this.projects.set([]);
          this.investmentsLoadingState.set(LOADED_LOADING_STATE);
        },
      });
  }

  private aggregateIncomingOffersCounts(
    investments: Investment[],
  ): Record<string, number> {
    const counts: Record<string, number> = {};

    for (const investment of investments) {
      const projectId = investment.projectId?.trim();
      if (!projectId) {
        continue;
      }

      counts[projectId] = (counts[projectId] ?? 0) + 1;
    }

    return counts;
  }
}
